import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { 
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, 
  BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator 
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { useSingleRFPData } from "@/hooks/useRFPData";
import { RFPEstimate } from "@/types/project-types";
import { componentTypes } from "@/data/estimationData";

import EstimationTab from "@/components/rfp/EstimationTab";
import AnalyticsTab from "@/components/rfp/AnalyticsTab";
import ReportTab from "@/components/rfp/ReportTab";

const RFPDetail = () => {
  const { rfpId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'estimation';
  const [activeTab, setActiveTab] = useState(initialTab);
  const { rfp, estimates, isLoading, error, updateRFP, updateEstimates } = useSingleRFPData(rfpId);
  
  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });
  
  const [componentEstimates, setComponentEstimates] = useState<RFPEstimate[]>(
    componentTypes.map(type => ({
      id: "",
      rfp_id: rfpId || "",
      component_type: type,
      simple_count: 0,
      medium_count: 0,
      complex_count: 0,
      supercomplex_count: 0,
      total_points: 0,
      total_dev_days: 0,
      total_days: 0,
      created_at: "",
      updated_at: ""
    }))
  );

  React.useEffect(() => {
    if (rfp) {
      setFormData({
        name: rfp.name,
        description: rfp.description || ""
      });
    }
  }, [rfp]);

  React.useEffect(() => {
    if (estimates && estimates.length > 0) {
      const estimatesMap = new Map(
        estimates.map(est => [est.component_type, est])
      );
      
      setComponentEstimates(
        componentTypes.map(type => {
          const existingEstimate = estimatesMap.get(type);
          if (existingEstimate) {
            return existingEstimate;
          }
          return {
            id: "",
            rfp_id: rfpId || "",
            component_type: type,
            simple_count: 0,
            medium_count: 0,
            complex_count: 0,
            supercomplex_count: 0,
            total_points: 0,
            total_dev_days: 0,
            total_days: 0,
            created_at: "",
            updated_at: ""
          };
        })
      );
    }
  }, [estimates, rfpId]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/rfp/${rfpId}?tab=${value}`);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const saveRFPInfo = async () => {
    if (!formData.name) {
      toast({
        title: "Error",
        description: "RFP name is required",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateRFP({ 
        name: formData.name, 
        description: formData.description || null 
      });
      
      toast({
        title: "Success",
        description: "RFP information updated successfully",
      });
    } catch (error) {
      console.error('Error updating RFP:', error);
      toast({
        title: "Error",
        description: "Failed to update RFP information",
        variant: "destructive",
      });
    }
  };

  const saveEstimation = async () => {
    try {
      const validEstimates = componentEstimates.filter(est => 
        est.simple_count > 0 || 
        est.medium_count > 0 || 
        est.complex_count > 0 || 
        est.supercomplex_count > 0
      );
      
      await updateEstimates(validEstimates);
      
      toast({
        title: "Success",
        description: "Estimation saved successfully",
      });
    } catch (error) {
      console.error('Error saving estimation:', error);
      toast({
        title: "Error",
        description: "Failed to save estimation",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-5xl py-8 flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !rfp) {
    return (
      <div className="container max-w-5xl py-8 text-center">
        <h2 className="text-xl font-semibold text-red-600">Error loading presales estimation</h2>
        <p className="mt-2 text-gray-600">{error?.message || "RFP not found"}</p>
        <Button 
          onClick={() => navigate("/presales")} 
          className="mt-4"
        >
          Back to Presales
        </Button>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-8 space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/presales">Presales Estimations</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>RFP Details</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate("/presales")} 
              className="border-blue-200 dark:border-gray-700 hover:bg-blue-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Presales
            </Button>
          </div>
        </div>
        <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-700 dark:from-gray-300 dark:to-gray-400 bg-clip-text text-transparent">
          {rfp.name}
        </h1>
      </div>

      <Tabs defaultValue={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="estimation">Estimation</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="report">Report</TabsTrigger>
        </TabsList>
        
        <TabsContent value="estimation">
          <EstimationTab 
            rfpId={rfpId}
            formData={formData}
            componentEstimates={componentEstimates}
            onFormChange={handleFormChange}
            onSaveRFPInfo={saveRFPInfo}
            onUpdateEstimate={setComponentEstimates}
            onSaveEstimation={saveEstimation}
          />
        </TabsContent>
        
        <TabsContent value="analytics">
          <AnalyticsTab componentEstimates={componentEstimates} />
        </TabsContent>
        
        <TabsContent value="report">
          <ReportTab 
            formData={formData}
            componentEstimates={componentEstimates}
            version={rfp?.version || 1}
            updatedAt={rfp?.updated_at || new Date().toISOString()}
          />
        </TabsContent>
      </Tabs>

      <div className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
        © {new Date().getFullYear()} <a href="https://cardyai.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">cardyai.com</a>. All rights reserved. | v1.2.0
      </div>
    </div>
  );
};

export default RFPDetail;
